/**
 * Product hero — single product page. Adds the +/- quantity stepper around
 * WooCommerce's own quantity input and the "Informacje prawne" expand
 * toggle. The favorite-toggle button rendered here (data-favorite-toggle)
 * is wired by template-parts/favorites/index.js, not this file — one
 * delegated listener shared with product-tile and the "Ulubione" listing.
 *
 * The piece/carton unit toggle and the quantity input itself are
 * WooCommerce's own markup, rendered via woocommerce_single_product_summary
 * — see inc/carton-packaging.php's own index.js for that behavior.
 */

import './index.scss';

const hero = document.querySelector('.product-hero');

if (hero) {
	initQuantityStepper(hero);
	initLegalToggle(hero);
	initAjaxAddToCart(hero);
}

/**
 * Route the native single-product add-to-cart form through WooCommerce's
 * own AJAX flow (assets/js/frontend/add-to-cart.js) instead of a full page
 * reload, so the "Produkt dodany do koszyka" toast (src/js/add-to-cart-
 * toast.js, already listening for the "added_to_cart" event that flow
 * fires) shows here too — that event only ever fires from an AJAX add,
 * never from a native form submit/reload, which is what this page did
 * before.
 *
 * Reuses WC core's own click handler (bound site-wide on
 * ".add_to_cart_button:not(.wc-interactive)") rather than writing a second
 * AJAX call: adding its expected classes/data-* to the existing submit
 * button is enough — its own preventDefault() on the click cancels the
 * form's native submit too. Kept in sync live (quantity, and the
 * piece/carton toggle's "nitai_unit" — inc/carton-packaging.php's
 * convert_submitted_quantity()/add_cart_item_data() filters read
 * $_REQUEST['nitai_unit'], which WC's own handler forwards from the
 * button's own data-* attributes) since those can change after page load,
 * unlike the static data-quantity="1" a loop/tile button gets away with.
 *
 * Skipped for variable products — WC's loop AJAX handler has no concept of
 * a variation_id, so forcing it here would silently add the wrong item.
 *
 * @param {HTMLElement} hero The .product-hero root.
 */
function initAjaxAddToCart(hero) {
	const form = hero.querySelector('form.cart');
	const button = form?.querySelector('.single_add_to_cart_button');
	const productIdField = form?.querySelector('[name="add-to-cart"]');

	if (!form || !button || !productIdField || form.querySelector('[name="variation_id"]')) return;

	button.classList.add('add_to_cart_button', 'ajax_add_to_cart');
	button.dataset.product_id = productIdField.value;

	const syncQuantity = () => {
		const qtyInput = form.querySelector('input.qty');
		button.dataset.quantity = qtyInput ? qtyInput.value : '1';
	};

	const syncUnit = () => {
		const checkedUnit = form.querySelector('input[name^="nitai_unit"]:checked');
		if (checkedUnit) {
			button.dataset.nitai_unit = checkedUnit.value;
		}
	};

	syncQuantity();
	syncUnit();

	form.addEventListener('change', (event) => {
		if (event.target.matches('input.qty')) syncQuantity();
		if (event.target.matches('input[name^="nitai_unit"]')) syncUnit();
	});

	const title = document.querySelector('.product-hero__title')?.textContent;
	if (title) {
		button.dataset.product_name = title;
	}
}

/**
 * Wrap WooCommerce's quantity <input> with -/+ buttons that step it via the
 * native stepUp()/stepDown() — respects whatever min/step
 * inc/carton-packaging.php's piece/carton toggle has already set.
 *
 * WooCommerce renders the input as type="hidden" instead of type="number"
 * whenever min equals max (e.g. only 1 unit left in stock) — there's nothing
 * to step between. Switched to type="text" here so it still displays inside
 * the stepper like every other product, with both buttons disabled since
 * stepping can't change a locked quantity.
 *
 * @param {HTMLElement} hero The .product-hero root.
 */
function initQuantityStepper(hero) {
	const panel = hero.querySelector('.product-hero__price-panel');
	const input = panel?.querySelector('.quantity .qty');

	if (!panel || !input || input.closest('.product-hero__qty-stepper')) return;

	const isLocked = input.type === 'hidden';
	if (isLocked) {
		input.type = 'text';
		input.readOnly = true;
	}

	const wrapper = document.createElement('div');
	wrapper.className = 'product-hero__qty-stepper';

	const minus = document.createElement('button');
	minus.type = 'button';
	minus.className = 'product-hero__qty-btn';
	minus.textContent = '−';
	minus.disabled = isLocked;
	minus.setAttribute('aria-label', 'Zmniejsz ilość');

	const plus = document.createElement('button');
	plus.type = 'button';
	plus.className = 'product-hero__qty-btn';
	plus.textContent = '+';
	plus.disabled = isLocked;
	plus.setAttribute('aria-label', 'Zwiększ ilość');

	input.parentNode.insertBefore(wrapper, input);
	wrapper.append(minus, input, plus);

	if (isLocked) return;

	minus.addEventListener('click', () => {
		input.stepDown();
		input.dispatchEvent(new Event('change', { bubbles: true }));
	});

	plus.addEventListener('click', () => {
		input.stepUp();
		input.dispatchEvent(new Event('change', { bubbles: true }));
	});
}

/**
 * "Informacje prawne" expand/collapse — same plain hidden-attribute
 * approach as template-parts/category-seo-toggle.
 *
 * @param {HTMLElement} hero The .product-hero root.
 */
function initLegalToggle(hero) {
	const toggle = hero.querySelector('[data-product-hero-legal-toggle]');
	const content = hero.querySelector('[data-product-hero-legal-content]');

	if (!toggle || !content) return;

	toggle.addEventListener('click', () => {
		const expanded = toggle.getAttribute('aria-expanded') === 'true';

		toggle.setAttribute('aria-expanded', String(!expanded));
		toggle.classList.toggle('is-open', !expanded);
		content.hidden = expanded;
	});
}
